/**
 * Purchase-order list — `/purchasing/purchase-orders`.
 *
 * Server-driven cursor DataTable over `GET /api/v1/purchase-orders` with the
 * contract's own filters (status, supplier_id, expected_from/to, q) and its
 * allowlisted `-field` sort tokens. Totals render through MoneyDisplay from
 * the wire decimal strings; no amount is ever parsed into a JS number.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { MoneyDisplay } from "@/components/money/money-display";

import {
  PURCHASE_ORDER_STATUSES,
  type PurchaseOrderStatus,
  type PurchaseOrderSummary,
} from "../api";
import { usePurchaseOrderList, type PurchaseOrderFilters } from "../queries";
import { useSupplierNames } from "../components/lookup-names";
import { PurchaseOrderStatusBadge } from "../components/status-badges";
import { enumLabel } from "../labels";

const ALL = "__all__";

export interface PoListPageProps {
  onOpenPurchaseOrder: (purchaseOrderId: string) => void;
  onCreatePurchaseOrder: () => void;
  canCreate?: boolean;
  canReadSuppliers?: boolean;
  /** Page size override (tests exercise cursor paging with a small limit). */
  pageLimit?: number;
  /** `?supplier_id=` deep link from partner 360 / reorder conversion. */
  initialSupplierId?: string;
  initialStatus?: PurchaseOrderStatus;
}

export function PoListPage({
  onOpenPurchaseOrder,
  onCreatePurchaseOrder,
  canCreate = false,
  canReadSuppliers = true,
  pageLimit,
  initialSupplierId,
  initialStatus,
}: PoListPageProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<PurchaseOrderStatus | typeof ALL>(initialStatus ?? ALL);
  const [supplierId, setSupplierId] = useState<string | null>(initialSupplierId ?? null);
  const [expectedFrom, setExpectedFrom] = useState("");
  const [expectedTo, setExpectedTo] = useState("");
  const [sort, setSort] = useState<SortState>(null);

  const { supplierName, parties } = useSupplierNames(canReadSuppliers);
  const supplierOptions = useMemo(
    () => parties.map((party) => ({ value: party.id, label: party.display_name })),
    [parties],
  );

  const filters = useMemo<PurchaseOrderFilters>(() => {
    const built: PurchaseOrderFilters = {};
    if (q !== "") built.q = q;
    if (status !== ALL) built.status = status;
    if (supplierId !== null && supplierId !== "") built.supplier_id = supplierId;
    if (expectedFrom !== "") built.expected_from = expectedFrom;
    if (expectedTo !== "") built.expected_to = expectedTo;
    return built;
  }, [q, status, supplierId, expectedFrom, expectedTo]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = usePurchaseOrderList(filters, sort, pageLimit);

  const columns = useMemo<ColumnDef<PurchaseOrderSummary>[]>(
    () => [
      {
        accessorKey: "po_number",
        id: "po_number",
        header: t("purchasing.poList.columns.number", "PO #"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "supplier_id",
        id: "supplier",
        header: t("purchasing.poList.columns.supplier", "Supplier"),
        enableSorting: false,
        cell: (ctx) => textCell(supplierName(ctx.getValue<string | undefined>())),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("purchasing.poList.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => <PurchaseOrderStatusBadge status={ctx.getValue<PurchaseOrderStatus>()} />,
      },
      {
        accessorKey: "grand_total",
        id: "grand_total",
        header: t("purchasing.poList.columns.total", "Total"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => (
          <MoneyDisplay
            value={{ amount: ctx.getValue<string>(), currency: ctx.row.original.currency }}
          />
        ),
      },
      {
        accessorKey: "order_date",
        id: "order_date",
        header: t("purchasing.poList.columns.orderDate", "Ordered"),
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "expected_delivery_date",
        id: "expected_delivery_date",
        header: t("purchasing.poList.columns.expected", "Expected"),
        cell: (ctx) => textCell(ctx.getValue<string | undefined>()),
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: t("purchasing.poList.columns.created", "Created"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    [t, supplierName],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("purchasing.poList.title", "Purchase orders")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("purchasing.poList.subtitle", "Draft, approve, send and receive supplier orders.")}
          </p>
        </div>
        {canCreate ? (
          <Button onClick={onCreatePurchaseOrder}>
            {t("purchasing.poList.newPo", "New purchase order")}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-po-q">
            {t("purchasing.poList.filters.q", "PO number or reference")}
          </Label>
          <Input
            id="purchasing-po-q"
            dir="ltr"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t("purchasing.poList.filters.qPlaceholder", "PO-2026-000001")}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-po-status">
            {t("purchasing.poList.filters.status", "Status")}
          </Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as PurchaseOrderStatus | typeof ALL)}
          >
            <SelectTrigger id="purchasing-po-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("purchasing.common.all", "All")}</SelectItem>
              {PURCHASE_ORDER_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {enumLabel(t, "poStatus", value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadSuppliers ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-supplier">
              {t("purchasing.poList.filters.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-po-supplier"
              options={supplierOptions}
              value={supplierId}
              onChange={setSupplierId}
              placeholder={t("purchasing.common.anySupplier", "Any supplier")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-po-expected-from">
            {t("purchasing.poList.filters.expectedFrom", "Expected from")}
          </Label>
          <Input
            id="purchasing-po-expected-from"
            type="date"
            dir="ltr"
            value={expectedFrom}
            onChange={(event) => setExpectedFrom(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-po-expected-to">
            {t("purchasing.poList.filters.expectedTo", "Expected to")}
          </Label>
          <Input
            id="purchasing-po-expected-to"
            type="date"
            dir="ltr"
            value={expectedTo}
            onChange={(event) => setExpectedTo(event.target.value)}
          />
        </div>
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setQ("");
              setStatus(ALL);
              setSupplierId(null);
              setExpectedFrom("");
              setExpectedTo("");
            }}
          >
            {t("purchasing.common.clearFilters", "Clear filters")}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => onOpenPurchaseOrder(row.id)}
        storageKey="purchasing.purchase-orders.list.columns"
        emptyMessage={
          hasFilters
            ? t(
                "purchasing.poList.emptyFiltered",
                "No purchase orders match. Clear a filter and try again.",
              )
            : t("purchasing.poList.empty", "No purchase orders yet.")
        }
        errorMessage={t("purchasing.poList.loadError", "Purchase orders could not be loaded.")}
      />
    </div>
  );
}
